import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Save, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { toast } from "sonner";
import { projectsApi, exteriorApi } from "@/lib/api";
import { formatCurrency, ITEM_LABELS, ITEM_UNITS, PAINT_TYPES, PREP_LEVELS, EXTERIOR_CATEGORIES } from "@/lib/utils";

export default function ExteriorEditor() {
  const { projectId, sideId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [side, setSide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [estimate, setEstimate] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadData(); }, [projectId, sideId]);

  const loadData = async () => {
    try {
      const projectRes = await projectsApi.getById(projectId);
      setProject(projectRes.data);
      const foundSide = projectRes.data.exterior_sides?.find(s => s.id === sideId);
      if (!foundSide) { toast.error("Exterior side not found"); navigate(`/project/${projectId}`); return; }
      setSide(foundSide);
      const estimateRes = await projectsApi.getEstimate(projectId);
      setEstimate(estimateRes.data);
    } catch (error) {
      toast.error("Failed to load data");
      navigate(`/project/${projectId}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDimensionChange = useCallback((field, value) => {
    const numValue = parseFloat(value) || 0;
    setSide(prev => {
      const updated = { ...prev, [field]: numValue };
      const length = field === 'length' ? numValue : prev.length;
      const height = field === 'height' ? numValue : prev.height;
      const sqFt = length * height;
      
      // Linear feet items that should match the length dimension
      const linearItems = ['ext_eaves_single', 'ext_eaves_two', 'ext_fascia_single', 'ext_fascia_two', 'ext_gutters', 'ext_wrought_iron'];
      // Square footage items that should match length * height
      const sqFtItems = ['ext_stucco_single', 'ext_siding_single', 'ext_stucco_two', 'ext_siding_two'];
      
      const updatedItems = prev.items.map(item => {
        if (linearItems.includes(item.item_type)) {
          return { ...item, quantity: length };
        }
        if (sqFtItems.includes(item.item_type)) {
          return { ...item, quantity: sqFt };
        }
        return item;
      });
      return { ...updated, items: updatedItems };
    });
  }, []);

  const handleItemChange = useCallback((itemType, field, value) => {
    setSide(prev => ({
      ...prev,
      items: prev.items.map(item => item.item_type === itemType ? { ...item, [field]: value } : item)
    }));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await exteriorApi.update(projectId, sideId, {
        name: side.name, length: side.length, height: side.height, items: side.items, notes: side.notes || "",
      });
      toast.success("Exterior side saved");
      loadData();
    } catch (error) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const getSideEstimate = () => {
    if (!estimate?.exterior_breakdowns) return null;
    return estimate.exterior_breakdowns.find(eb => eb.side_id === sideId);
  };

  const renderItemGroup = (categoryName, itemTypes, skipHeading = false) => {
    const categoryItems = side.items?.filter(item => itemTypes.includes(item.item_type)) || [];
    if (categoryItems.length === 0) return null;

    return (
      <div key={categoryName} className="space-y-3">
        {!skipHeading && <h3 className="text-xs uppercase tracking-widest text-muted-foreground font-medium border-b border-border pb-2">{categoryName}</h3>}
        {categoryItems.map((item) => (
          <div key={item.item_type} className={`p-4 border rounded-sm transition-colors ${item.enabled ? 'border-amber-500/50 bg-card' : 'border-border bg-muted/20'}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <Switch checked={item.enabled} onCheckedChange={(checked) => handleItemChange(item.item_type, 'enabled', checked)} />
                <span className="font-medium text-sm">{ITEM_LABELS[item.item_type] || item.item_type}</span>
              </div>
              {item.enabled ? (
                <span className="text-xs px-2 py-1 rounded-sm bg-green-500/20 text-green-400 border border-green-500/30"><Check className="w-3 h-3 inline mr-1" />Included</span>
              ) : (
                <span className="text-xs px-2 py-1 rounded-sm bg-muted text-muted-foreground border border-border"><X className="w-3 h-3 inline mr-1" />Excluded</span>
              )}
            </div>
            {item.enabled && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="input-group">
                  <Label className="input-label text-xs">Qty ({ITEM_UNITS[item.item_type] || "units"})</Label>
                  <Input type="number" value={item.quantity} onChange={(e) => handleItemChange(item.item_type, 'quantity', parseFloat(e.target.value) || 0)} className="font-mono h-9" />
                </div>
                <div className="input-group">
                  <Label className="input-label text-xs">Coats</Label>
                  <Select value={String(item.coats)} onValueChange={(v) => handleItemChange(item.item_type, 'coats', parseInt(v))}>
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 Coat</SelectItem>
                      <SelectItem value="2">2 Coats</SelectItem>
                      <SelectItem value="3">3 Coats</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="input-group">
                  <Label className="input-label text-xs">Paint Type</Label>
                  <Select value={item.paint_type} onValueChange={(v) => handleItemChange(item.item_type, 'paint_type', v)}>
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>{PAINT_TYPES.filter(p => p.value.startsWith('ext') || p.value === 'primer').map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="input-group">
                  <Label className="input-label text-xs">Prep Level</Label>
                  <Select value={String(item.prep_level)} onValueChange={(v) => handleItemChange(item.item_type, 'prep_level', parseInt(v))}>
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>{PREP_LEVELS.map(c => <SelectItem key={c.value} value={String(c.value)}>{c.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 md:col-span-4 flex items-center gap-3 pt-1">
                  <Switch checked={item.needs_primer} onCheckedChange={(checked) => handleItemChange(item.item_type, 'needs_primer', checked)} />
                  <Label className="text-sm">Needs Primer</Label>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  if (loading) return <div className="page-container flex items-center justify-center min-h-screen"><div className="animate-pulse text-muted-foreground">Loading...</div></div>;
  if (!side || !project) return null;

  const sideEstimate = getSideEstimate();
  const moreItemsDefaultOpen = [
    ["Preparation", EXTERIOR_CATEGORIES.prep],
    ["Eaves & Fascia", EXTERIOR_CATEGORIES.eaves_fascia],
    ["Windows & Doors", EXTERIOR_CATEGORIES.windows_doors],
    ["Miscellaneous", EXTERIOR_CATEGORIES.misc],
  ].filter(([, itemTypes]) => side.items?.some(i => itemTypes.includes(i.item_type) && i.enabled))
   .map(([label]) => label);

  return (
    <div className="page-container">
      <header className="app-header">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(`/project/${projectId}`)}><ArrowLeft className="w-5 h-5" /></Button>
            <div>
              <h1 className="font-heading text-xl font-bold tracking-tight">{side.name}</h1>
              <p className="text-xs text-muted-foreground">{project.name} • Exterior</p>
            </div>
          </div>
          <Button onClick={handleSave} className="btn-primary" disabled={saving}><Save className="w-4 h-4 mr-2" />{saving ? "Saving..." : "Save"}</Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader><CardTitle className="font-heading text-lg">Side Details</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="input-group">
                  <Label className="input-label">Side Name</Label>
                  <Select value={side.name} onValueChange={(v) => setSide({ ...side, name: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Front">Front</SelectItem>
                      <SelectItem value="Back">Back</SelectItem>
                      <SelectItem value="Left Side">Left Side</SelectItem>
                      <SelectItem value="Right Side">Right Side</SelectItem>
                      <SelectItem value="Garage">Garage</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="input-group"><Label className="input-label">Length (ft)</Label><Input type="number" value={side.length} onChange={(e) => handleDimensionChange('length', e.target.value)} className="font-mono" /></div>
                  <div className="input-group"><Label className="input-label">Height (ft)</Label><Input type="number" value={side.height} onChange={(e) => handleDimensionChange('height', e.target.value)} className="font-mono" /></div>
                </div>
                <div className="p-3 bg-secondary/50 rounded-sm"><p className="text-xs uppercase tracking-wider text-muted-foreground">Total Area</p><p className="font-mono text-lg">{(side.length * side.height).toFixed(0)} sq ft</p></div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="font-heading text-lg">Exterior Items</CardTitle></CardHeader>
              <CardContent className="space-y-6">
                {renderItemGroup("Body Paint", EXTERIOR_CATEGORIES.body)}
                <Accordion type="multiple" defaultValue={moreItemsDefaultOpen} className="space-y-0">
                  {[
                    ["Preparation", EXTERIOR_CATEGORIES.prep],
                    ["Eaves & Fascia", EXTERIOR_CATEGORIES.eaves_fascia],
                    ["Windows & Doors", EXTERIOR_CATEGORIES.windows_doors],
                    ["Miscellaneous", EXTERIOR_CATEGORIES.misc],
                  ].map(([label, itemTypes]) => {
                    const enabledCount = side.items?.filter(i => itemTypes.includes(i.item_type) && i.enabled).length || 0;
                    return (
                      <AccordionItem key={label} value={label}>
                        <AccordionTrigger className="text-xs uppercase tracking-widest text-muted-foreground font-medium hover:no-underline">
                          <span>{label}{enabledCount > 0 && <span className="ml-2 text-amber-500 normal-case">({enabledCount} included)</span>}</span>
                        </AccordionTrigger>
                        <AccordionContent>{renderItemGroup(label, itemTypes, true)}</AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="font-heading text-lg">Notes</CardTitle></CardHeader>
              <CardContent>
                <Textarea value={side.notes || ""} onChange={(e) => setSide({ ...side, notes: e.target.value })} placeholder="Add notes..." rows={3} />
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card className="sticky-panel">
              <CardHeader><CardTitle className="font-heading text-lg">Side Estimate</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                {sideEstimate ? (
                  <>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center"><span className="text-xs uppercase tracking-wider text-muted-foreground">Dimensions</span><span className="font-mono text-sm">{sideEstimate.dimensions}</span></div>
                      <div className="flex justify-between items-center"><span className="text-xs uppercase tracking-wider text-muted-foreground">Labor Hours</span><span className="font-mono">{sideEstimate.total_hours} hrs</span></div>
                      <div className="flex justify-between items-center"><span className="text-xs uppercase tracking-wider text-muted-foreground">Labor Cost</span><span className="font-mono">{formatCurrency(sideEstimate.labor_cost)}</span></div>
                    </div>
                    {Object.keys(sideEstimate.material_gallons || {}).length > 0 && (
                      <div className="border-t border-border pt-4">
                        <p className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Materials</p>
                        <div className="space-y-2">
                          {Object.entries(sideEstimate.material_gallons).map(([type, gallons]) => (
                            <div key={type} className="flex justify-between items-center text-sm"><span className="capitalize">{type.replace('_', ' ')}</span><span className="font-mono">{gallons} gal</span></div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="border-t border-border pt-4">
                      <div className="flex justify-between items-center"><span className="text-xs uppercase tracking-wider text-muted-foreground">Material Cost</span><span className="font-mono">{formatCurrency(sideEstimate.material_cost)}</span></div>
                    </div>
                    <div className="border-t border-border pt-4">
                      <div className="flex justify-between items-center"><span className="font-heading font-semibold">Side Total</span><span className="font-mono text-xl text-amber-500">{formatCurrency(sideEstimate.labor_cost + sideEstimate.material_cost)}</span></div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-4"><p className="text-muted-foreground text-sm">Save to see estimate</p></div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
